import * as React from 'react';
import { format, parse, isValid, set as setTime } from 'date-fns';
import { CalendarIcon, X } from 'lucide-react';

import { cn } from '../../lib/utils';
import { Button } from './button';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';

const DATE_FORMAT = 'yyyy-MM-dd';
const DATETIME_FORMAT = "yyyy-MM-dd'T'HH:mm";

const parseValue = (value, pattern) => {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  const parsed = parse(value, pattern, new Date());
  return isValid(parsed) ? parsed : null;
};

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

/**
 * Trigger shared by both pickers. Shows the formatted value, or the placeholder
 * when empty, with a clear affordance once something is chosen.
 */
function PickerTrigger({ display, placeholder, disabled, onClear, id, className, open }) {
  return (
    <PopoverTrigger asChild>
      <Button
        id={id}
        type="button"
        variant="outline"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cn(
          'group w-full justify-start gap-2 px-3 font-normal',
          !display && 'text-muted-foreground',
          className
        )}
      >
        <CalendarIcon className="h-4 w-4 shrink-0 opacity-60" />
        <span className="truncate">{display || placeholder}</span>
        {display && !disabled && (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Clear date"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClear();
            }}
            className="ml-auto rounded-sm p-0.5 opacity-0 transition-opacity hover:bg-accent group-hover:opacity-60"
          >
            <X className="h-3.5 w-3.5" />
          </span>
        )}
      </Button>
    </PopoverTrigger>
  );
}

/**
 * DatePicker — drop-in replacement for `<Input type="date">`.
 *
 * Takes and returns a `yyyy-MM-dd` string, so it swaps straight into forms that
 * already hold dates that way, but renders our own calendar rather than the
 * browser's native picker.
 */
function DatePicker({
  value,
  onChange,
  placeholder = 'Pick a date',
  disabled = false,
  id,
  className,
  // Compliance dates land a year or more out, so paging month by month is the
  // wrong tool — this swaps the caption for month and year dropdowns.
  monthYearNav = false,
  yearsBack = 5,
  yearsForward = 12,
  ...calendarProps
}) {
  const [open, setOpen] = React.useState(false);
  const selected = parseValue(value, DATE_FORMAT);

  const navProps = monthYearNav
    ? {
        captionLayout: 'dropdown',
        startMonth: new Date(new Date().getFullYear() - yearsBack, 0),
        endMonth: new Date(new Date().getFullYear() + yearsForward, 11)
      }
    : {};

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PickerTrigger
        id={id}
        open={open}
        display={selected ? format(selected, 'd MMM yyyy') : ''}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        onClear={() => onChange('')}
      />
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected || undefined}
          defaultMonth={selected || undefined}
          onSelect={(date) => {
            onChange(date ? format(date, DATE_FORMAT) : '');
            setOpen(false);
          }}
          autoFocus
          {...navProps}
          {...calendarProps}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * DateTimePicker — drop-in replacement for `<Input type="datetime-local">`.
 *
 * Calendar plus a time row in the same popover. Minutes step in fives, which is
 * finer than any pickup slot needs and keeps the list short enough to scan.
 */
function DateTimePicker({
  value,
  onChange,
  placeholder = 'Pick a date and time',
  disabled = false,
  id,
  className,
  ...calendarProps
}) {
  const [open, setOpen] = React.useState(false);
  const selected = parseValue(value, DATETIME_FORMAT);

  const emit = (date) => onChange(date ? format(date, DATETIME_FORMAT) : '');

  const handleDate = (date) => {
    if (!date) {
      emit(null);
      return;
    }
    // Picking a day keeps the time already chosen; a first pick lands at 9am,
    // which is when a courtesy car realistically changes hands.
    const base = selected || setTime(new Date(), { hours: 9, minutes: 0 });
    emit(setTime(date, { hours: base.getHours(), minutes: base.getMinutes(), seconds: 0, milliseconds: 0 }));
  };

  const handleTime = (unit, next) => {
    const base = selected || setTime(new Date(), { hours: 9, minutes: 0, seconds: 0, milliseconds: 0 });
    emit(setTime(base, unit === 'hour' ? { hours: Number(next) } : { minutes: Number(next) }));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PickerTrigger
        id={id}
        open={open}
        display={selected ? format(selected, 'd MMM yyyy, h:mm a') : ''}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        onClear={() => emit(null)}
      />
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected || undefined}
          defaultMonth={selected || undefined}
          onSelect={handleDate}
          autoFocus
          {...calendarProps}
        />
        <div className="flex items-center gap-2 border-t p-3">
          <span className="text-xs font-medium text-muted-foreground">Time</span>
          <div className="ml-auto flex items-center gap-1">
            <Select
              value={selected ? String(selected.getHours()).padStart(2, '0') : undefined}
              onValueChange={(v) => handleTime('hour', v)}
            >
              <SelectTrigger className="h-8 w-16">
                <SelectValue placeholder="09" />
              </SelectTrigger>
              <SelectContent className="max-h-56">
                {HOURS.map((h) => (
                  <SelectItem key={h} value={h}>
                    {h}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-muted-foreground">:</span>
            <Select
              value={selected ? String(Math.floor(selected.getMinutes() / 5) * 5).padStart(2, '0') : undefined}
              onValueChange={(v) => handleTime('minute', v)}
            >
              <SelectTrigger className="h-8 w-16">
                <SelectValue placeholder="00" />
              </SelectTrigger>
              <SelectContent className="max-h-56">
                {MINUTES.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { DatePicker, DateTimePicker };
